import React, { useState, useRef, useEffect } from 'react';
import { useTextSize } from '../contexts/TextSizeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Type, X, Move, Eye, EyeOff, Globe } from 'lucide-react';

const TextSizeControl = () => {
  const { textSize, setTextSize } = useTextSize();
  const { language, setLanguage, languageOptions } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isVisible, setIsVisible] = useState(true);
  const [showTabText, setShowTabText] = useState(() => {
    const saved = localStorage.getItem('showTabText');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const dragRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const textSizeOptions = [
    { value: 'large', label: '大', description: 'Large' },
    { value: 'medium', label: '中', description: 'Medium' },
    { value: 'small', label: '小', description: 'Small' },
    { value: 'tiny', label: '微', description: 'Tiny' }
  ];

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't start drag if clicking on buttons
    
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;
    
    // Keep window within viewport bounds
    const maxX = window.innerWidth - 300; // Approximate width
    const maxY = window.innerHeight - 180; // Increased height for new controls
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Save position to localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem('textSizeControlPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition(parsed);
      } catch (e) {
        console.warn('Failed to parse saved position');
      }
    }
  }, []);

  // Save position when it changes
  useEffect(() => {
    localStorage.setItem('textSizeControlPosition', JSON.stringify(position));
  }, [position]);

  // Save tab text preference
  useEffect(() => {
    localStorage.setItem('showTabText', JSON.stringify(showTabText));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('tabDisplayChanged', { 
      detail: { showText: showTabText } 
    }));
  }, [showTabText]);

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed top-4 right-4 z-50 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
        title="Show Text Size Control"
      >
        <Type className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div
      ref={dragRef}
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 cursor-move"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        userSelect: isDragging ? 'none' : 'auto',
        pointerEvents: isDragging ? 'none' : 'auto'
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header with drag handle and close button */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Move className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500 font-medium">拖拽移動</span>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Text size controls */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Type className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">文字大小:</span>
          <div className="flex space-x-1">
            {textSizeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTextSize(option.value)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  textSize === option.value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={`${option.description} (${option.label})`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab display control */}
        <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            {showTabText ? (
              <Eye className="w-4 h-4 text-gray-500" />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-500" />
            )}
            <span className="text-sm font-medium text-gray-700">標籤顯示:</span>
          </div>
          <div className="flex space-x-1">
            <button
              onClick={() => setShowTabText(true)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                showTabText
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Show tab text"
            >
              文字
            </button>
            <button
              onClick={() => setShowTabText(false)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                !showTabText
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Show icons only"
            >
              圖標
            </button>
          </div>
        </div>

        {/* Language control */}
        <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">語言:</span>
          </div>
          <div className="flex space-x-1">
            {languageOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setLanguage(option.value)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  language === option.value
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={`${option.description} (${option.label})`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextSizeControl; 